import Image from "next/image";
import type { Reaction } from "@memento/types/reaction";
import type { Card } from "@memento/types/card";
import { Button } from "@memento/ui/Button";
import { SimpleLink } from "src/components/SimpleLink";
import { getPluralEnding } from "@memento/library/text";
import Link from "next/link";
import { cn } from "src/utils/cn";

export type DisplayPublishedReactionsProps = {
  addVideoUrl: string;
  card: Card;
  publishedReactions: Reaction[];
  pendingCount: number;
  reactionType: string;
  isRecipientView?: boolean;
  compilationId: string;
  addReactionVideoAllowed: boolean;
};

type DisplayPendingReactionsProps = {
  pendingCount: number;
  publishedCount: number;
};

const DisplayPendingReactions = (props: DisplayPendingReactionsProps) => {
  const { pendingCount, publishedCount } = props;
  // if there are only pending reactions, we need height/width to be set for display
  const onlyPending = publishedCount === 0 && pendingCount > 0;

  return (
    <div
      className={cn(
        `flex flex-col items-center justify-center rounded-xl bg-[#9aa6b0] text-center`,
        onlyPending ? `py-4` : ``
      )}
    >
      <Image
        src="/images/icons/icnVideoCamera.svg"
        alt="Record Icon"
        width={40}
        height={40}
      />
      <div className="text-xs text-white">
        {pendingCount} video{getPluralEnding(pendingCount)}
      </div>
      <div className="text-xs text-white">pending approval...</div>
    </div>
  );
};

export const DisplayPublishedReactions = ({
  reactionType,
  addVideoUrl,
  publishedReactions = [],
  pendingCount,
  isRecipientView = false,
  compilationId,
  addReactionVideoAllowed,
}: DisplayPublishedReactionsProps) => {
  const hasPublishedOrPendingReactions =
    publishedReactions.length > 0 || pendingCount > 0;

  if (
    !hasPublishedOrPendingReactions ||
    (!publishedReactions.length && !isRecipientView)
  ) {
    return null;
  }

  return (
    <div className="mx-auto mt-8 rounded-2xl bg-white p-6 shadow-lg lg:w-2/3">
      <div className="flex flex-row items-center justify-between text-left text-lg">
        {reactionType} Videos
        {addReactionVideoAllowed ? (
          <Link href={addVideoUrl}>
            <Button variant="primary" width="responsive">
              Add Video
            </Button>
          </Link>
        ) : null}
      </div>
      <div className="mt-4 flex flex-col justify-center text-center">
        <div className="grid grid-cols-2 gap-2 sm:grid sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-4">
          {/* only recipients will be able to see the pending reactions */}
          {pendingCount > 0 && isRecipientView ? (
            <DisplayPendingReactions
              pendingCount={pendingCount}
              publishedCount={publishedReactions.length}
            />
          ) : null}
          {publishedReactions.map((reaction) => (
            <SimpleLink
              className="w-full overflow-hidden rounded-xl"
              path={`/reactions/${compilationId}`}
              key={reaction.reactionId}
              data-test="view-reaction-link"
            >
              <img
                src={reaction.thumbUrl.s3}
                alt="Reaction video thumbnail"
                className="h-full w-full object-cover"
              />
            </SimpleLink>
          ))}
        </div>
      </div>
    </div>
  );
};
